import { ContractVersionViewModel, ContractViewModel } from '@ail/contracts-api'
import { skipToken } from '@reduxjs/toolkit/query'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Arr } from '../../../helpers/Arr'
import { isDateValid, printDate } from '../../../helpers/Utils'
import { useAuth } from '../../../hooks/useAuth'
import { useGetContractVehicleDriversQuery } from '../../../redux/api/contractsApi'
import { useGetAllUnitsQuery } from '../../../redux/api/customerApi'

export function DriverDetails(props: { contract?: ContractViewModel; onCopy: (value?: string) => void }) {
  const [showDriversPopup, setShowDriversPopup] = useState(false)
  const [showHistory, setShowHistory] = useState(false)

  const { user } = useAuth()
  const { t } = useTranslation('common')
  const units = useGetAllUnitsQuery()
  const drivers = useGetContractVehicleDriversQuery(
    props.contract?.vehicle ? { contractId: props.contract.id, vehicleId: props.contract.vehicle.id } : skipToken
  )

  const currentContractCostCenter = useMemo(() => {
    if (!props.contract || !units.currentData) {
      return undefined
    }
    return units.currentData.find((u) => u.value && props.contract?.owner === u.value)?.unit
  }, [props.contract, units.currentData])

  const costCenterMap = useMemo(() => {
    const versions = props.contract?.versions?.filter((v) => !!v.owner) ?? []
    return Arr.toHashMap(
      versions,
      (v) => v.owner!,
      (v) => units.currentData?.find((u) => u.value === v.owner)?.unit
    )
  }, [props.contract, units.currentData])

  const currentVehicleDriver = useMemo(() => {
    return props.contract?.vehicle?.driver
  }, [props.contract])

  const vehicleDriversHistory = useMemo(() => {
    if (!props.contract || !drivers.currentData) {
      return []
    }

    const now = new Date().getTime()
    const contractStart = new Date(props.contract.startDate).getTime()

    return drivers.currentData.filter((d) => {
      const validFrom = new Date(d.validFrom).getTime()
      const validTo = d.validTo ? new Date(d.validTo).getTime() : 0
      const validAlways = -62135596800000
      const dateCorrect =
        validFrom !== -5364664500000 &&
        validFrom >= contractStart &&
        ((validTo >= validFrom && validFrom <= validTo) || validTo === validAlways)

      return dateCorrect && validFrom < now && now > validTo
    })
  }, [drivers.currentData, props.contract])

  const filteredVersions = useMemo(() => {
    const contract = props.contract
    if (!contract || !contract.versions?.some((q) => q.owner !== contract.owner)) {
      return []
    }

    const sorted = Arr.sort(contract.versions, (v) => new Date(v.validFrom), 'DESC').map((v) => ({ ...v }))

    const result: ContractVersionViewModel[] = []
    for (const version of sorted) {
      const firstVersion = result.at(-1)
      if (!firstVersion || firstVersion.owner !== version.owner) {
        result.push(version)
      } else {
        if (result.filter((el) => el.owner === version.owner).length > 1) {
          firstVersion.validFrom =
            new Date(version.validFrom).getTime() < new Date(result[result.length - 1]?.validFrom).getTime()
              ? result[result.length - 1].validFrom
              : version.validFrom
        }
        if (new Date(version.validFrom).getTime() < new Date(firstVersion.validFrom).getTime()) {
          firstVersion.validFrom = version.validFrom
        }
        if (
          version.validTo &&
          firstVersion.validTo &&
          new Date(version.validTo).getTime() > new Date(firstVersion.validTo).getTime()
        ) {
          firstVersion.validTo = version.validTo
        }
      }
    }
    return result
  }, [props.contract])

  return (
    <div className="detailContentArea driverArea">
      <div className="detailTitle">
        <i className="icon-driver" /> <span>{props.contract?.poolCar ? t('POOL_CAR') : t('DRIVER_DETAILS')}</span>
        {vehicleDriversHistory?.length > 0 && (
          <div className="historyArea">
            <i className="icon-history" onClick={() => setShowDriversPopup(!showDriversPopup)}></i>
            {showDriversPopup && (
              <div className="tooltip">
                <ul className="lastMile">
                  {vehicleDriversHistory?.map((el, i) => (
                    <li key={i}>
                      <div className="flex">
                        <div className="typeMile">
                          <i className="icon-user"></i>
                          {el.displayName}
                        </div>
                        <div className="dataMile">
                          {el.validTo && new Date(el.validTo).getFullYear() === 1
                            ? null
                            : t('TO').toLowerCase() + ' ' + printDate(el.validTo)}
                          <br />
                          {t('FROM').toLowerCase()} {printDate(new Date(el.validFrom))}
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
      </div>
      <div className="details">
        <div className="row">
          {props.contract?.poolCar && !props.contract?.location ? null : (
            <div
              className="col-6"
              style={props.contract?.vehicle?.driverName && { cursor: 'pointer' }}
              onClick={() => props.onCopy(props.contract?.vehicle?.driver?.displayName)}
            >
              <label style={props.contract?.vehicle?.driver?.displayName ? { cursor: 'pointer' } : undefined}>
                {!props.contract?.poolCar ? t('DRIVER') : props.contract?.location ? t('LOCATION') : t('POOL_CAR')}
              </label>
              {props.contract?.poolCar === undefined ? (
                ''
              ) : props.contract?.poolCar ? (
                props.contract?.location || ''
              ) : props.contract?.vehicle?.driver?.displayName ? (
                props.contract?.vehicle?.driver?.displayName
              ) : (
                <div className="flex">
                  {t('NO_DRIVER')} <i className="icon-denied" />{' '}
                </div>
              )}
            </div>
          )}
          <div className="col-6">
            <label>{t('COST_CENTER')}</label>
            {currentContractCostCenter?.name === undefined ? (
              ''
            ) : (
              <div
                className={`costC ${
                  !!filteredVersions.length &&
                  filteredVersions.some((q) => new Date(q.validFrom).getTime() > new Date().getTime())
                    ? 'toBeChanged'
                    : ''
                }`}
              >
                {currentContractCostCenter?.name ? (
                  <span>{currentContractCostCenter?.name}</span>
                ) : (
                  <div className="flex">
                    {' '}
                    {t('NO_COST_CENTER')} <i className="icon-denied" />{' '}
                  </div>
                )}
                {!!filteredVersions.length && (
                  <i
                    style={{
                      color: `${
                        filteredVersions.filter((q) => new Date(q.validFrom).getTime() > new Date().getTime()).length >
                        0
                          ? ''
                          : 'black'
                      }`,
                    }}
                    className="icon-change-generic"
                    onClick={() => setShowHistory(!showHistory)}
                  >
                    {showHistory && (
                      <div className="tooltip">
                        <ul className="lastMile">
                          {filteredVersions.map((q, i) => (
                            <li key={i}>
                              <div className="flex">
                                <div className="dataMile">{costCenterMap[q.owner!]?.name ?? t('NO_COST_CENTER')}</div>
                                <div className="dataMile">
                                  {!isDateValid(q.validTo) && isDateValid(q.validFrom) && (
                                    <>
                                      {t('TO')} {printDate(q.validTo)}
                                      <br />
                                    </>
                                  )}

                                  {!isDateValid(q.validFrom) && q.owner
                                    ? t('FIRST_VERSION')
                                    : `${t('FROM')} ${printDate(q.validFrom)}`}
                                </div>
                              </div>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </i>
                )}
              </div>
            )}
          </div>
          {props.contract?.poolCar && !!props.contract?.managers.length && (
            <div className="col-6">
              <label>{t('OBJECT_MANAGER')}</label>
              {user?.isInternalUser &&
              props.contract?.managers[0]?.contacts?.filter((el) => el?.kind === 'Email')?.length > 0 ? (
                <a
                  href={`mailto:${
                    props.contract?.managers[0]?.contacts?.filter((el) => el?.kind === 'Email')[0]?.value
                  }`}
                >
                  {props.contract?.managers[0].displayName}
                </a>
              ) : (
                props.contract?.managers[0].displayName
              )}
            </div>
          )}
        </div>
        {!props.contract?.poolCar && !!currentVehicleDriver && (
          <div className="row">
            {!!currentVehicleDriver.employeeNumber && (
              <div className="col-6">
                <label>{t('EMPLOYEE_NUMBER')}</label>
                <div>{currentVehicleDriver.employeeNumber}</div>
              </div>
            )}
            {!!currentVehicleDriver.vehicleUsageRight &&
              currentVehicleDriver.vehicleUsageRight.toLowerCase() !== 'unspecified' && (
                <div className="col-6">
                  <label>{t('USAGE_RIGHT')}</label>
                  <div>{currentVehicleDriver.vehicleUsageRight}</div>
                </div>
              )}
          </div>
        )}
      </div>
    </div>
  )
}
